import type { SxProps, Theme } from '@mui/material';
import { Box, Divider } from '@mui/material';
import { CardSection, CardSubSection } from '@side/pantry';
import { useMemo } from 'react';
import { DataGrid } from '@/components/DataGrid';
import { PerformanceFilterValuesFooter } from '../../FilterValuesFooter/FilterValuesFooter';
import { useComparisonView } from '../hooks/useComparisonView';
import {
  getComparisonViewColumns,
  LOADING_ROWS,
} from './ComparisonView.helpers';

export const ComparisonView = ({
  viewReportAs,
  sx,
}: {
  viewReportAs: string;
  sx?: SxProps<Theme>;
}) => {
  const { data, isLoading, currentPeriodLabel, pastPeriodLabel } =
    useComparisonView({
      viewReportAs,
    });

  const columns = useMemo(
    () => getComparisonViewColumns({ currentPeriodLabel, pastPeriodLabel }),
    [currentPeriodLabel, pastPeriodLabel],
  );

  return (
    <Box sx={{ ...sx, display: 'flex', height: '100%' }}>
      <CardSection
        cardTitle="Comparison View"
        id="comparison-view-card-section"
        sx={{ flex: 1 }}
      >
        <CardSubSection sx={{ pt: 2 }}>
          <DataGrid
            loading={isLoading}
            columns={columns}
            rows={isLoading ? LOADING_ROWS : data}
            variant="simple"
            hideFooter
            disableColumnMenu
            disableColumnResize
            disableColumnSorting
            sx={{
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
                borderBottom: 'none',
              },
              // MuiDataGrid sets overflow: hidden as an inline style on column headers,
              // which CSS class selectors cannot override without !important.
              // This allows the GlossaryTooltip ::after underline (positioned at bottom: -4px)
              // to render outside the column header's bounds.
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent':
                {
                  overflow: 'visible !important',
                },
              '& .MuiDataGrid-row:hover': {
                cursor: 'default',
                backgroundColor: 'unset',
              },
              '& .MuiDataGrid-columnHeader:hover': {
                cursor: 'default',
                backgroundColor: 'common.white',
              },
            }}
          />
          <Divider />
          <PerformanceFilterValuesFooter sx={{ pt: 4 }} />
        </CardSubSection>
      </CardSection>
    </Box>
  );
};
